import React from 'react';
import { useModal } from 'react-modal-hook';
import styled from 'styled-components';
import { Button, Icon, Text } from 'components';
import { useFeatureFlags } from 'contexts/featureFlags';
import { theme, toRem } from 'design';
import { FeatureFlags, IEmployerSite, PermissionType } from 'types';
import { hasSitePermission } from 'utils';
import { Title } from '../../../styles';
import { AIDaySegmentsModal } from './AIDaySegmentsModal';

interface ISuggestDaySegmentsProps {
  currentSite: IEmployerSite;
}

const Layout = styled.div`
  flex-direction: column;
  align-items: flex-start;
  padding: ${toRem(theme.space.xl)};
  gap: ${toRem(theme.space.lg)};
  background: ${theme.color.primary[25]};
  border: ${toRem(1)} solid ${theme.color.primary[500]};
  border-radius: ${toRem(8)};
  align-self: stretch;
  flex-grow: 0;
  margin-top: ${toRem(theme.space.lg)};

  p {
    color: ${theme.color.neutral[600]};
  }
`;

const StyledButton = styled(Button)`
  width: ${toRem(200)} !important;
`;

const StyledIcon = styled(Icon)`
  position: relative;
  margin-right: ${toRem(theme.space.sm)};
  fill: ${theme.color.primary[600]};
  top: ${toRem(3)};
`;

export const SuggestDaySegments: React.FC<ISuggestDaySegmentsProps> = ({ currentSite }) => {
  const suggestDaySegmentsPermission = hasSitePermission(currentSite?.permissions, PermissionType.GENERATE_DAY_SEGMENTS);
  const suggestDaySegmentsEnabled = useFeatureFlags()[FeatureFlags.AI_DAY_SEGMENTS_SUGGEST]?.enabled;

  // Do not display if we do not have permissions or the feature flag is not enabled
  if (!suggestDaySegmentsPermission || !suggestDaySegmentsEnabled) return <></>;

  const [showDaySegmentsLoader, hideDaySegmentsLoader] = useModal(() => <AIDaySegmentsModal siteId={currentSite.id} onClose={hideDaySegmentsLoader} />, [currentSite]);

  return (
    <Layout data-cy="ai-day-segments-suggestion">
      <Title fontWeight="medium">
        <StyledIcon icon="Wizard" />
        Day Segments Generator
      </Title>
      <Text size="xsmall">Automatically generate day segments using your point of sale data. Let AI analyze your labor patterns to suggest consistent day segments — eliminating guesswork and accelerating setup.</Text>
      <StyledButton appearance="primary" size="small" label="Suggest Day Segments" data-cy="ai-day-segments-button" onClick={showDaySegmentsLoader} />
    </Layout>
  );
};
